import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink } from "lucide-react";

const TYPES = ["Music Video", "Film", "OTT", "Advertisement", "Fashion", "Campaign", "Photoshoot", "Web Series", "Other"];

const BLANK = { title: "", artist: "", featuring: "", music: "", lyrics: "", label: "",
                 category: "Music Video", views: "", youtube_url: "", featured_home: false };

export default function AdminProjectsTab() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/works").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setF({ ...f, [k]: v });

  const create = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    setBusy(true);
    try { await api.post("/admin/works", { ...f, casting_director: "Senty Virk" });
      toast.success("Project added"); setF(BLANK); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  const update = async (work_id, patch) => { await api.post("/admin/works/update", { work_id, ...patch }); load(); };
  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/admin/works/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-xs text-[#8A8478]">{items.length} projects</div>
        <button onClick={() => setShowForm(!showForm)} data-testid="add-project" className="sv-btn-primary inline-flex items-center gap-2">
          <Plus size={14} />{showForm ? "Cancel" : "Add Project"}
        </button>
      </div>

      {showForm && (
        <div className="sv-card p-6 grid md:grid-cols-2 gap-3">
          <input required placeholder="Title *" className="sv-input" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="pr-title" />
          <input placeholder="Artist / Client" className="sv-input" value={f.artist} onChange={(e) => set("artist", e.target.value)} data-testid="pr-artist" />
          <select className="sv-select" value={f.category} onChange={(e) => set("category", e.target.value)}>
            {TYPES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input placeholder="Featuring" className="sv-input" value={f.featuring} onChange={(e) => set("featuring", e.target.value)} />
          <input placeholder="Music" className="sv-input" value={f.music} onChange={(e) => set("music", e.target.value)} />
          <input placeholder="Lyrics" className="sv-input" value={f.lyrics} onChange={(e) => set("lyrics", e.target.value)} />
          <input placeholder="Label" className="sv-input" value={f.label} onChange={(e) => set("label", e.target.value)} />
          <input placeholder="Views (e.g. 5M+)" className="sv-input" value={f.views} onChange={(e) => set("views", e.target.value)} />
          <input placeholder="YouTube URL (https://…)" className="sv-input md:col-span-2" value={f.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} data-testid="pr-yt" />
          <label className="flex items-center gap-2 text-xs text-[#CDC7BA]">
            <input type="checkbox" checked={f.featured_home} onChange={(e) => set("featured_home", e.target.checked)} /> Feature on Home
          </label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="sv-btn-outline">Cancel</button>
            <button onClick={create} disabled={busy} data-testid="pr-save" className="sv-btn-primary">{busy ? "Saving…" : "Save Project"}</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 && <div className="text-sm text-[#8A8478]">No projects yet.</div>}
        {items.map((w) => (
          <div key={w.work_id} className="sv-card p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div>
              <div className="font-mono-label text-[10px] text-[#C5A059]">{w.category || "Music Video"}</div>
              <div className="font-serif-editorial text-lg text-[#F4EFE4] mt-1">{w.title}</div>
              <div className="text-xs text-[#8A8478]">{w.artist}{w.views && ` · ${w.views}`}</div>
              <div className="text-[10px] font-mono-label mt-1">
                {w.youtube_url ? <span className="text-[#7BC46B]">● WATCH ACTIVE</span> : <span className="text-[#8A8478]">○ NO URL</span>}
                {w.featured_home && <span className="text-[#C5A059] ml-3">HOME</span>}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <input placeholder="YouTube URL" defaultValue={w.youtube_url}
                onBlur={(e) => e.target.value !== w.youtube_url && update(w.work_id, { youtube_url: e.target.value })}
                className="sv-input text-xs w-64" />
              <label className="text-xs text-[#CDC7BA] flex items-center gap-2">
                <input type="checkbox" defaultChecked={w.featured_home} onChange={(e) => update(w.work_id, { featured_home: e.target.checked })} /> Home
              </label>
              {w.youtube_url && <a href={w.youtube_url} target="_blank" rel="noopener noreferrer" className="sv-btn-outline text-xs inline-flex items-center gap-1"><ExternalLink size={12} />Preview</a>}
              <button onClick={() => del(w.work_id)} className="sv-btn-outline text-xs inline-flex items-center gap-1"><Trash2 size={12} />Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
